import * as React from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/taskpane/components/ui/dialog";
import { Button } from "@/taskpane/components/ui/button";
import { useAddTopUp } from "@/hooks/useAddTopUp";
import { Loader, InfoIcon } from "lucide-react";
import { Input } from "@/taskpane/components/ui/input";
import { Field, FieldContent, FieldError, FieldLabel } from "@/taskpane/components/ui/field";
import { toast } from "sonner";

interface TopUpCreditsDialogProps extends React.ComponentPropsWithoutRef<typeof Dialog> { }

const topUpSchema = z.object({
    amount: z.coerce
        .number({ invalid_type_error: "Amount must be a number" })
        .int("Amount must be a whole number")
        .positive("Amount must be positive")
        .min(1, "Minimum amount is 1"),
});

type TopUpFormData = z.infer<typeof topUpSchema>;

const TopUpCreditsDialog = ({ ...props }: TopUpCreditsDialogProps) => {
    const dialogContentRef = React.useRef<HTMLDivElement>(null);
    const addTopUpMutation = useAddTopUp();

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset
    } = useForm<TopUpFormData>({
        resolver: zodResolver(topUpSchema),
        defaultValues: {
            amount: 100, // Default value as per previous behavior
        },
        mode: "onChange"
    });

    const onSubmit = async (data: TopUpFormData) => {
        try {
            const response = await addTopUpMutation.mutateAsync(data.amount);
            if (response?.url) {
                window.open(response.url, "_blank");
                toast.success("Redirecting to payment...");
            } else {
                toast.success("Credits added successfully");
            }
            props.onOpenChange?.(false);
        } catch (error) {
            toast.error("Failed to add credits");
        }
    };

    // Reset form when dialog opens/closes
    React.useEffect(() => {
        if (!props.open) {
            reset();
        }
    }, [props.open, reset]);


    return (
        <Dialog {...props}>
            <DialogContent showCloseButton={true} ref={dialogContentRef}>
                <DialogHeader>
                    <img
                        src="../../assets/aila-logo-updated.png"
                        alt="Logo"
                        className="w-[130px] h-auto mb-4 mx-auto"
                    />
                    <DialogTitle>Exhausted Credits</DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        You have run out of credits. Please enter the amount to top up.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Field data-invalid={fieldState.invalid} className="gap-1.5">
                                <FieldLabel>Amount</FieldLabel>
                                <FieldContent>
                                    <Input
                                        type="number"
                                        placeholder="Enter amount"
                                        {...field}
                                        disabled={addTopUpMutation.isPending}
                                        className="h-10"
                                    />
                                    {fieldState.invalid && (
                                        <div className="flex items-center gap-1 mt-1">
                                            <InfoIcon className="inline size-3 text-danger-200" />
                                            <FieldError className="leading-4" errors={[fieldState.error]} />
                                        </div>
                                    )}
                                </FieldContent>
                            </Field>
                        )}
                    />

                    <DialogFooter className="flex-col sm:flex-col gap-2 mt-4">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={addTopUpMutation.isPending || !isValid}
                        >
                            {addTopUpMutation.isPending ? (
                                <>
                                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                                    Adding Credits...
                                </>
                            ) : (
                                "Add Credits"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default TopUpCreditsDialog;
